// The one phone-number rule, mirroring app/models/phone_number.rb and fisiohome-backend
// #219 (NormalizePhoneNumber, isValidPhone). Do not validate with libphonenumber's
// isValidPhoneNumber: it passes "+6262812345678" as an Indonesian landline.

const INDONESIAN = /^\+62(8\d{8,11}|[2-79]\d{6,9})$/;
const FOREIGN = /^\+[1-9]\d{7,14}$/;

export const PHONE_FORMAT_MESSAGE =
	"Invalid phone number. Write a foreign number with + and its country code, e.g. +61 412 345 678";

/** "+<digits>", or "" when the input cannot be read as a number. A foreign number written
 * without "+" also gives "": prefixing it with +62 would make "+6261…". */
export const normalizePhone = (phone: string): string => {
	const first = (phone.split(/[/,;]/).find((part) => part !== "") ?? "").trim();
	let plus = first.startsWith("+");
	let digits = first.replace(/\D/g, "");
	if (!digits) return "";

	if (!plus && digits.startsWith("00") && digits.length > 2) {
		digits = digits.slice(2);
		plus = true;
	}
	if (!plus && !/^(0|62|8)/.test(digits)) return "";
	if (plus && !digits.startsWith("62")) return `+${digits}`.slice(0, 50);

	let national = digits.startsWith("62") ? digits.slice(2) : digits;
	// "62" + a mobile-length "628…" is a doubled country code; a short one is the Kabanjahe area code 0628.
	if (national.startsWith("628") && national.length >= 11)
		national = national.slice(2);
	if (national.startsWith("0")) national = national.slice(1);
	return national ? `+62${national}`.slice(0, 50) : "";
};

/** What a paste should set the field to, or null to let the browser paste. A paste
 * replaces the field when it carries its own country code, or when the field holds
 * nothing but the pre-filled "+62" once the selection is cut out; otherwise "+62" and a
 * pasted "62812…" join into "+6262812…". A fragment pasted into a number is left alone. */
export const pastedPhone = (
	current: string,
	selectionStart: number,
	selectionEnd: number,
	pasted: string,
): string | null => {
	const normalized = normalizePhone(pasted);
	if (!normalized) return null;
	const kept = (
		current.slice(0, selectionStart) + current.slice(selectionEnd)
	).replace(/\D/g, "");
	const ownCode = /^\s*(\+|00)/.test(pasted);
	return ownCode || kept === "" || kept === "62" ? normalized : null;
};

export const isValidPhone = (phone: string): boolean =>
	phone.startsWith("+62") ? INDONESIAN.test(phone) : FOREIGN.test(phone);
